/**
 * Merchant 文件上传 / 预览 / 删除（US-002）。
 * 契约：multipart `file` + `slot`；GET /files/content?key= 带 Bearer 拉 blob，禁止 window.open 无 token。

 * FE-E2b 薄壳化：共享类型 + mock blob 工具 + filename 解析 + 上传模板下沉
 * @shared/api/filesCommon；fetchContent/delete 保留本地（裸 fetch credentials:omit 通道）。
 */
import {
  mockImageBlob,
  mockPdfBlob,
  looksLikePdf,
  parseContentDispositionFileName,
  uploadFile,
  type FileUploadResult,
  type FileUploadSlot,
  type FileScanStatus,
  type FileContentResult,
} from '@shared/api/filesCommon';

export type { FileUploadSlot, FileScanStatus, FileUploadResult, FileContentResult };

import {
  loadSession,
  merchantApiUrl,
  merchantDelete,
  merchantUpload,
  USE_MOCK,
} from './client';

export async function uploadMerchantFile(
  file: File,
  slot: FileUploadSlot,
  directory = 'merchant',
) {
  return uploadFile(merchantUpload, file, slot, directory);
}

/** 带 Bearer 拉 content blob，对齐 agentGetBlob 语义。 */
export async function fetchMerchantFileContent(key: string): Promise<FileContentResult> {
  if (USE_MOCK) {
    await Promise.resolve();
    return {
      ok: true,
      blob: looksLikePdf(key) ? mockPdfBlob() : mockImageBlob(),
    };
  }
  try {
    const s = loadSession();
    const headers: Record<string, string> = {
      Accept: 'image/*,application/pdf,application/octet-stream,*/*',
    };
    if (s?.accessToken) {
      headers.Authorization = `Bearer ${s.accessToken}`;
    }
    const qs = new URLSearchParams({ key });
    const res = await fetch(merchantApiUrl(`/files/content?${qs.toString()}`), {
      method: 'GET',
      headers,
      credentials: 'omit',
    });
    if (res.ok) {
      const bytes = await res.arrayBuffer();
      const contentType = (res.headers.get('Content-Type') || 'application/octet-stream')
        .split(';')[0]
        .trim();
      return {
        ok: true,
        blob: new Blob([bytes], { type: contentType || 'application/octet-stream' }),
        fileName: parseContentDispositionFileName(res.headers.get('Content-Disposition')),
      };
    }
    let error = `HTTP ${res.status}`;
    let code: string | undefined;
    try {
      const body = (await res.json()) as { code?: string | number; message?: string };
      if (body?.message) {
        error = body.message;
      } else if (body?.code !== undefined) {
        error = String(body.code);
      }
      if (body?.code !== undefined) {
        code = String(body.code);
      }
    } catch {
      // 内容流错误体可能不是 JSON
    }
    return { ok: false, error, status: res.status, code };
  } catch (e) {
    return { ok: false, error: e instanceof Error ? e.message : 'network_error', status: 0 };
  }
}

export async function deleteMerchantFile(key: string) {
  const qs = new URLSearchParams({ key });
  return merchantDelete<void>(`/files?${qs.toString()}`, () => undefined);
}

export { USE_MOCK };
